import { Autocomplete, Box, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceV3 } from '../../../../../backendsdk';
import useDevices from '../../../../../hooks/devices';
import { normalizeLicensePlate } from '../../../../../utils/Str';

export interface VehicleSelectorProps {
    onSelect: (device: DeviceV3 | null) => void;
    label?: string;
    value?: DeviceV3 | null;
    /** When true, show warning when selected device has no current_trip (e.g. for verify flow) */
    showDeviceNotTurnedOnWarning?: boolean;
}

const VehicleSelector: React.FC<VehicleSelectorProps> = (props) => {
    const { devices } = useDevices();
    const { t } = useTranslation();

    const sortedDevices = React.useMemo(
        () =>
            [...devices].sort((a, b) =>
                normalizeLicensePlate(a.device.license_plate).localeCompare(
                    normalizeLicensePlate(b.device.license_plate),
                ),
            ),
        [devices],
    );

    const label = props.label ?? t('setup_tool.installation_params.license_plate');

    return (
        <Box
            sx={{
                width: '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Autocomplete
                fullWidth
                id="vehicle-selector-field"
                data-testid="vehicle-selector"
                options={sortedDevices}
                value={props.value ?? null}
                onChange={(_e, value) => props.onSelect(value)}
                renderInput={(params) => <TextField {...params} fullWidth label={label} />}
                filterOptions={(options, params) =>
                    options.filter((option: DeviceV3) =>
                        normalizeLicensePlate(option.device.license_plate).includes(
                            normalizeLicensePlate(params.inputValue),
                        ),
                    )
                }
                getOptionLabel={(option: DeviceV3) => option.device.license_plate || ''}
                sx={{ mt: 1 }}
            />
            {props.showDeviceNotTurnedOnWarning && props.value && !props.value.current_trip ? (
                <Box sx={{ mt: 1, width: '100%' }}>
                    <Box
                        component="span"
                        fontSize={14}
                        textAlign="center"
                        color="error.main"
                        sx={{ fontWeight: 500, display: 'block' }}
                    >
                        {t('setup_tool.verify_camera.device_not_turned_on')}
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
};

export default VehicleSelector;
